import { FieldSet } from "@prismicio/editor-ui"
import {
  Document,
  GroupContent,
  isGroupContent,
  isNestableContent,
  isTextContent,
  // isUIDContent,
  NestableContent,
  UIDContent,
} from "@prismicio/types-internal/lib/content"
import type { StaticCustomType } from "@prismicio/types-internal/lib/customtypes"
import { useEffect, useMemo, useRef } from "react"

import { MetadataPreview } from "../core/SeoMetadata/components/MetadataPreview"
import {
  extractTextFromField,
  type MetadataType,
  metaDescriptionProperty,
  metaTitleProperty,
} from "../core/service"
import { useEditorConfig } from "../EditorConfig"
import { MetadataTextField } from "../fields/MetadataTextField"
// import { UIDField } from "../fields/UIDField"
import { GroupEditor } from "../GroupEditor"
import { NestableWidgetSwitch } from "../NestableWidgetSwitch"
import type { StaticZone, StaticZoneContent } from "./StaticZone"

interface StaticZoneEditorProps {
  id: string
  readOnly: boolean
  staticZone: StaticZone
  content: StaticZoneContent
  onContentChange: (content: StaticZoneContent) => void
  documentId: string
  documentContent: Document
  documentCustomType: StaticCustomType
  documentLocale: string
}

export function StaticZoneEditor(props: StaticZoneEditorProps) {
  const {
    id,
    readOnly,
    staticZone,
    content,
    onContentChange,
    documentId,
    documentContent,
    documentCustomType,
    documentLocale,
  } = props
  const config = useEditorConfig()
  const staticWidgets = useMemo(() => Object.entries(staticZone), [staticZone])
  const contentRef = useRef(content)

  useEffect(() => {
    contentRef.current = content
  }, [content])

  if (staticWidgets.length === 0) return null

  const isSEOAndMetadataTab = isStaticZoneSEOAndMetadataTab(staticWidgets)

  const existingMetaTitleField =
    extractTextFromField(content[metaTitleProperty]) || "Meta title.."

  const existingMetaDescriptionField =
    extractTextFromField(content[metaDescriptionProperty]) ||
    "Meta description.."

  return (
    <FieldSet legend="Page data">
      {isSEOAndMetadataTab && (
        <MetadataPreview
          title={existingMetaTitleField}
          description={existingMetaDescriptionField}
        />
      )}
      {staticWidgets.map(([staticWidgetId, staticWidget]) => {
        const staticWidgetProps = {
          id: `${id}.${staticWidgetId}`,
          onContentChange: (
            staticWidgetContent:
              | UIDContent
              | GroupContent
              | NestableContent
              | undefined,
          ) => {
            const { [staticWidgetId]: _, ...prevContent } = contentRef.current
            onContentChange({
              ...prevContent,
              ...(staticWidgetContent !== undefined && {
                [staticWidgetId]: staticWidgetContent,
              }),
            })
          },
          readOnly,
        }
        const staticWidgetContent = content[staticWidgetId]

        if (staticWidget.type === "UID") {
          return null
        } else if (staticWidget.type === "Group") {
          return (
            <GroupEditor
              {...staticWidgetProps}
              content={
                isGroupContent(staticWidgetContent)
                  ? staticWidgetContent
                  : undefined
              }
              group={staticWidget}
              key={staticWidgetId}
            />
          )
        } else {
          if (
            config.aiSeoMetadataEnabled &&
            isSeoMetadataWidget(staticWidgetId) &&
            staticWidget.type === "Text"
          ) {
            return (
              <MetadataTextField
                {...staticWidgetProps}
                content={
                  isTextContent(staticWidgetContent)
                    ? staticWidgetContent
                    : undefined
                }
                field={staticWidget}
                key={staticWidgetId}
                metadataFieldType={staticWidgetId}
                documentId={documentId}
                documentContent={documentContent}
                documentCustomType={documentCustomType}
                documentLocale={documentLocale}
              />
            )
          }

          return (
            <NestableWidgetSwitch
              {...staticWidgetProps}
              content={
                isNestableContent(staticWidgetContent)
                  ? staticWidgetContent
                  : undefined
              }
              key={staticWidgetId}
              nestableWidget={staticWidget}
            />
          )
        }
      })}
    </FieldSet>
  )
}

function isSeoMetadataWidget(
  staticWidgetId: string,
): staticWidgetId is MetadataType {
  return (
    staticWidgetId === metaTitleProperty ||
    staticWidgetId === metaDescriptionProperty
  )
}

const isStaticZoneSEOAndMetadataTab = (
  staticWidgets: [string, unknown][],
): boolean => {
  return (
    staticWidgets[0]?.[0] === metaTitleProperty &&
    staticWidgets[1]?.[0] === metaDescriptionProperty
  )
}
